interface StoryItem {
  eyebrow: string;
  title: string;
  body: string;
  image: string;
}

interface Props {
  sectionEyebrow: string;
  sectionTitle: React.ReactNode;
  sectionLead: string;
  items: StoryItem[];
  background?: "deep" | "elevated" | "default";
}

/**
 * Reusable alternating image/text narrative section.
 * Mirrors the editorial story pattern used across all service pages.
 */
export const StoryAlternating = ({
  sectionEyebrow,
  sectionTitle,
  sectionLead,
  items,
  background = "deep",
}: Props) => {
  const bg =
    background === "deep"
      ? "bg-background-deep"
      : background === "elevated"
      ? "bg-background-elevated"
      : "bg-background";

  return (
    <section className={`py-20 md:py-32 ${bg}`}>
      <div className="container-page">
        <div className="max-w-2xl mb-20">
          <div className="eyebrow">— {sectionEyebrow}</div>
          <h2 className="display-md mt-6 text-foreground text-balance">{sectionTitle}</h2>
          <p className="lead mt-6">{sectionLead}</p>
        </div>

        <div className="space-y-24 md:space-y-32">
          {items.map((s, i) => {
            const reverse = i % 2 === 1;
            return (
              <div
                key={s.title}
                className="grid gap-10 lg:gap-16 lg:grid-cols-12 items-center"
              >
                <div className={`lg:col-span-5 ${reverse ? "lg:order-2 lg:col-start-8" : ""}`}>
                  <div className="group relative mx-auto aspect-[4/3] w-full max-w-[520px] overflow-hidden rounded-3xl shadow-elevated ring-1 ring-foreground/5">
                    <img
                      src={s.image}
                      alt={s.title}
                      width={1040}
                      height={780}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.04]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/30 via-transparent to-transparent" />
                    <div className="absolute bottom-5 left-5 inline-flex items-center gap-2 rounded-full border border-background/20 bg-foreground/70 backdrop-blur px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] text-background">
                      {s.eyebrow}
                    </div>
                  </div>
                </div>
                <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}>
                  <div className="max-w-xl">
                    <div className="font-display text-6xl md:text-7xl text-[#1DABED] leading-none">
                      0{i + 1}
                    </div>
                    <h3 className="display-md mt-4 text-foreground text-balance">{s.title}</h3>
                    <p className="mt-6 text-base md:text-lg leading-[1.75] font-light text-foreground-muted">
                      {s.body}
                    </p>
                    <div className="mt-8 inline-flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-primary">
                      <span className="h-px w-8 bg-primary" />
                      Cloud Redovisning
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
